<script setup lang="ts">
import { createFormControl, type FormControl, type ValidatorFn } from '@/utils/forms';
import { generateUUID } from '@/utils/strings';
import { watch } from 'vue';
import { computed } from 'vue';

const props = withDefaults(defineProps<{
    control?: FormControl<string>;
    modelValue?: string;
    label?: string;
    icon?: string;
    type?: string;
    placeholder?: string;
    mask?: string | string[];
    disabled?: boolean;
    readOnly?: boolean;
    validators?: ValidatorFn[];
    autocomplete?: string;
    name?: string;
}>(), {
    type: 'text',
    icon: 'fas fa-font',
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'blur', value: any): void;
    (e: 'focus', value: any): void;
}>();

const _control = props.control ?? createFormControl(props.modelValue, props.validators);

watch(() => _control.value, (newValue) => {
    emit("update:modelValue", String(newValue ?? ''));
});

watch(() => props.modelValue, (newValue) => {
    if (!props.control && _control.value !== newValue) {
        _control.setValue(String(newValue ?? ''));
    }
});

const id = generateUUID();

const cssClass = computed(() => {

    return {
        'e-input-focus': _control.focused,
        'e-error': _control.errors.length && _control.touched,
        'e-disabled': props.disabled,
        'e-ready-only': props.readOnly,
    }
});
</script>

<template>
    <div>
        <label v-if="label" class="e-label" :for="id">
            {{ label }}
            <span class="e-error" v-if="_control.isRequired">*</span>
        </label>

        <div class="e-input-group" :class="cssClass">
            <span v-if="icon" class="e-input-group-icon" :class="icon"></span>

            <input v-if="!mask" :type="type" :id="id" :name="name" class="e-input" :placeholder="placeholder" :readonly="readOnly"
                :autocomplete="autocomplete"
                v-model="_control.value" @focus="_control.markAsFocused(); emit('focus', $event)"
                @blur="_control.markAsUnfocused(); emit('blur', _control.value)" @keydown.stop />

            <input v-else :type="type" :id="id" :name="name" class="e-input" :placeholder="placeholder" :readonly="readOnly"
                :autocomplete="autocomplete"
                v-model="_control.value" @focus="_control.markAsFocused(); emit('focus', $event)"
                @blur="_control.markAsUnfocused(); emit('blur', _control.value)" @keydown.stop v-mask="mask" />
        </div>

        <div v-if="_control.touched" class="e-error" v-for="error in _control.errors" :key="error">
            {{ error }}
        </div>
    </div>
</template>
